import {
  Box,
  Grid,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Flex,
  Spinner,
  VStack,
  Button,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select
} from "@chakra-ui/react";
import Banner from "views/admin/profile/components/Banner";
import banner from "assets/img/auth/banner.png";
import React, { useEffect, useState } from "react";
import { currentUser } from "utils/CurrentUser";
import { useParams } from "react-router-dom";
import { ENDPOINTS } from "utils/Constants";
import axios from "axios";
import { notify } from "components/ToastNotification";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_PUBLIC_KEY } from "utils/Constants";
import { useLocation } from "react-router-dom";
import { AVAILABLE_LANGUAGES } from "utils/Constants";
import { useI18n } from "i18n";


export default function Overview() {
  const { t, setLanguage } = useI18n();
  const [user, setUser] = useState({});
  const [isLoaded, setIsLoaded] = useState(false); // Loader state
  const [skillsData, setSkillsData] = useState({
    ongoingSkillsCount: 0,
    completedSkillsCount: 0,
    totalPoints: 0,
  });
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [pricingPlans, setPricinPlans] = useState([]);
  const { username: urlUsername } = useParams();
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCancelModalOpen, onOpen: onOpenCancelModal, onClose: onCloseCancelModal } =
    useDisclosure()
  const [planToCancel, setPlanToCancel] = useState(null);

  const [selectedLanguage, setSelectedLanguage] = useState("");


  const selectedPlanBg = useColorModeValue("blue.50", "blue.900")

  const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  const location = useLocation();
  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(
        `${ENDPOINTS.GET_PRICING_PLANS}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` },
        }
      );
      const { success, data, output } = response.data;
      if (success) {
        setPricinPlans(data);
      }

    } catch (error) {
      notify("Unable to fetch Pricing details.", "error");
    } finally {
      setIsLoaded(true);
    }
  };
  useEffect(() => {
    const verifyPayment = async (sessionId) => {
      notify("Verifying payment, please wait...")
      try {
        const response = await axios.post(
          `${ENDPOINTS.VERIFY_PAYMENT}`,
          { sessionId },
          {
            headers: { Authorization: `Bearer ${currentUser.token}` },
          }
        );
        const { success, output, data } = response.data;
        if (success) {
          fetchPricingPlans();
          localStorage.setItem("token", data.token);
          notify("Payment successful. Plan activated!", "success");
        } else {
          notify("Payment verification failed. Please try again.", "error");
        }
      } catch (error) {
        notify("Error verifying payment.", "error");
      }
    };
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session_id");
    if (sessionId) {
      verifyPayment(sessionId).finally(() => {
        const newUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, newUrl);
      });

    }
  }, [location.search]);

  useEffect(() => {
    const fetchUser = async () => {
      setIsLoaded(false);
      try {
        const response = await axios.get(
          `${ENDPOINTS.GET_USER}${urlUsername || ''}`,
          {
            headers: { Authorization: `Bearer ${currentUser.token}` },
          }
        );
        const { ongoingSkillsCount, completedSkillsCount, totalPoints, learningLanguage } = response.data.data;
        setSkillsData({
          ongoingSkillsCount,
          completedSkillsCount,
          totalPoints,
        });
        setUser(response.data.data);
        setSelectedLanguage(learningLanguage)
      } catch (error) {
        notify("Unable to fetch user details.", "error");
      } finally {
        setIsLoaded(true);
      }
    };
    fetchPricingPlans();
    fetchUser();
  }, [urlUsername]);

  const { ongoingSkillsCount, completedSkillsCount, totalPoints } = skillsData;
  const percentageCompletion = ongoingSkillsCount + completedSkillsCount
    ? Math.round((completedSkillsCount / (ongoingSkillsCount + completedSkillsCount)) * 100)
    : 0;

  const handleCheckout = async (planId) => {
    setIsLoadingCheckout(true);
    onOpen();
    try {
      const stripe = await stripePromise;
      // Assuming you have an endpoint that creates a Stripe checkout session
      const { data } = await axios.post(ENDPOINTS.CREATE_STRIPE_SESSION, {
        planId
      }, {
        headers: {
          Authorization: `Bearer ${currentUser.token}`, // Authorization header
        },
      });

      const { sessionId } = data.data;
      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        notify(result.error.message, "error");
      }
    } catch (error) {
      console.error(error);
      notify("Something went wrong during Stripe checkout.", "error");
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    setLanguage(newLanguage);

    try {
      await axios.post(
        ENDPOINTS.UPDATE_MY_LEARNING_LANGUAGE,
        { language: newLanguage },
        {
          headers: { Authorization: `Bearer ${currentUser.token}` },
        }
      );
      const currentUserRaw = localStorage.getItem("currentUser");
      const parsedCurrentUser = currentUserRaw ? JSON.parse(currentUserRaw) : null;
      if (parsedCurrentUser) {
        parsedCurrentUser.learningLanguage = newLanguage;
        localStorage.setItem("currentUser", JSON.stringify(parsedCurrentUser));
      }
      notify(t("common.saveSuccess", "Updated successfully"), "success");
    } catch (error) {
      notify("Failed to update learning language.", "error");
    }
  };

  const cancelSubscription = async () => {
    try {
      const result = await axios.post(`${ENDPOINTS.CANCEL_MY_SUBSCRIPTION}`, {}, {
        headers: { Authorization: `Bearer ${currentUser.token}` },
      });
      const { success, data, output } = result.data;
      if (success) {
        notify(output, 'success')
        fetchPricingPlans();
      } else {
        notify(output, 'error');
      }
    } catch (error) {
      notify("Failed to cancel subscription.", "error");
    }
    onCloseCancelModal();
  }

  const handleCancelClick = (planId) => {
    onOpenCancelModal();
  };

  const setFreePlan = (isCurrentPlan = false) => {
    !isCurrentPlan && onOpenCancelModal();
  }



  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} bg={cardBg} p="20px" borderRadius="10px" boxShadow="md" mb="20px">
      {/* Loader */}
      {!isLoaded ? (
        <Flex justifyContent="center" alignItems="center" h="200px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : (
        <>
          {/* Banner */}
          <Banner banner={banner} fullName={user.firstName + ' ' + user.lastName} />

          {/* Summary Section */}
          <Flex mt="30px" justifyContent={"space-between"}>
            <Stat display={"flex"} alignItems={"center"} alignContent="center" justifyContent={"center"}>
              <StatLabel>Ongoing Skills</StatLabel>
              <StatNumber>{ongoingSkillsCount}</StatNumber>
              <StatHelpText>Keep up the good work!</StatHelpText>
            </Stat>

            <Stat display={"flex"} alignItems={"center"} alignContent="center" justifyContent={"center"}>
              <StatLabel>Completed Skills</StatLabel>
              <StatNumber>{completedSkillsCount}</StatNumber>
              <StatHelpText>Great job completing skills!</StatHelpText>
            </Stat>

            <Stat display={"flex"} alignItems={"center"} alignContent="center" justifyContent={"center"}>
              <StatLabel>Total Points</StatLabel>
              <StatNumber>{totalPoints}</StatNumber>
              <StatHelpText>Points earned from quizzes</StatHelpText>
            </Stat>
          </Flex>

          {/* Overall Progress */}
          <Box mt="40px">
            <Text fontSize="xl" mb="10px">Overall Progress</Text>
            <Flex alignItems="center">
              <Progress value={percentageCompletion} colorScheme="blue" size="lg" flex="1" />
              <Text ml="10px">{percentageCompletion}%</Text>
            </Flex>
          </Box>

          {/* Language Selector */}
          {urlUsername == '' || urlUsername == currentUser.username ? (
            <>
              <Box mt="40px">
                <Text fontSize="xl" mb="10px">{t("common.learningLanguage", "Learning Language")}</Text>
                <Select
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                  placeholder={t("common.selectLanguage", "Select a language")}
                >
                  {AVAILABLE_LANGUAGES.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </Select>
              </Box>
            </>
          ) : (<></>)}


          {/* Pricing Plans */}
          {urlUsername == '' || urlUsername == currentUser.username ? (
            <Box mt="50px">
              <Text fontSize="2xl" fontWeight="bold" mb="20px">Pricing Plans</Text>
              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="20px">
                {pricingPlans.map((plan, index) => {
                  const isCurrentPlan = plan.subscribedPlan && !plan.isDefault;
                  const isFreePlan = plan.isDefault;

                  const handleButtonClick = () => {
                    if (isCurrentPlan) {
                      if (!plan.isPlanCanceled) {
                        handleCancelClick()
                      }
                      plan.isPlanCanceled && notify("Subscription plan already canceled.")
                    } else if (isFreePlan) {
                      setFreePlan(plan.subscribedPlan)
                    } else if (!plan.subscribedPlan) {
                      handleCheckout(plan.id);
                    }
                  };

                  return (
                    <Box
                      key={index}
                      p="20px"
                      borderRadius="10px"
                      bg={plan.subscribedPlan ? selectedPlanBg : cardBg}
                      boxShadow="md"
                      border={plan.subscribedPlan ? "2px solid blue" : "1px solid gray"}
                    >
                      <VStack spacing="10px">
                        <Text fontSize="xl" fontWeight="bold">{plan.title}</Text>
                        <Text fontSize="3xl" fontWeight="bold" color="blue.500">{plan.price}</Text>
                        <VStack spacing="5px" alignContent="center">
                          {plan.features.map((feature, idx) => (
                            <Text key={idx} fontSize="sm">{feature}</Text>
                          ))}
                        </VStack>
                        <Button
                          variant={"darkBrand"}
                          disabled={isFreePlan}
                          onClick={handleButtonClick}
                        >
                          {isCurrentPlan
                            ? (plan.isPlanCanceled ? "Canceled" : "Cancel Subscription")
                            : isFreePlan
                              ? (plan.subscribedPlan ? 'Your plan' : "Start Free")
                              : "Upgrade"}
                        </Button>
                        <Text fontSize="11px">
                          {plan.expiresIn}
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </Grid>
            </Box>

          ) : (<></>)}

        </>
      )}

      {/* Modal for Stripe checkout */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upgrading Plan</ModalHeader>
          <ModalBody>
            <Text>Wait till we make a secure connection for the transaction...</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isCancelModalOpen} onClose={onCloseCancelModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Cancellation</ModalHeader>
          <ModalBody>
            <Text>Are you sure you want to cancel your subscription?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onCloseCancelModal}>
              No, Keep it
            </Button>
            <Button colorScheme="red" onClick={cancelSubscription} ml={3}>
              Yes, Cancel it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
